import { defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { Device, type DeviceInfo } from '@capacitor/device'
import { SafeArea } from 'capacitor-plugin-safe-area'
import { usePreferencesStore } from '@/stores/preferences.ts'
import { serverUnavailable } from '@/vendor/serverAvailability'
import { radioConnectionType, radioOffline, readRadioStatusFromDevice } from '@/utils/networkStatus'
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { PushNotifications } from '@capacitor/push-notifications'
import { LocalNotifications } from '@capacitor/local-notifications'
import { clearAppBadge } from '@/utils/appBadge.ts'
import type { RouteLocationRaw } from 'vue-router'
import router, { RouteName } from '@/router'
import { createStartupNavigationIntent } from '@/stores/startupNavigation.ts'
import { useUiStore } from '@/stores/ui.store.ts'

const HIGH_NOTIFICATION_CHANNEL_ID = 'atsume_high'

// Tapping any notification lands on the list, never on the item it points at:
// the payload's deep-link fields stay unused until per-item routing is revisited.
const NOTIFICATION_TAP_ROUTE: RouteLocationRaw = { name: RouteName.Notifications }

export const useInfoStore = defineStore('info', () => {
  const platform = ref<DeviceInfo['platform']>('web')
  const isWeb = ref<boolean>(false)
  const isDev = ref<boolean>(import.meta.env.DEV)
  const fcmToken = ref<string | null>(null)
  const insets = ref<{ top: number; right: number; bottom: number; left: number }>({
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  })

  const { forceOffline } = storeToRefs(usePreferencesStore())

  const isOffline = computed(() => radioOffline.value || forceOffline.value)
  // The unified connectivity signal: reachable server AND online device AND not
  // forced offline. False means serve offline data / queue writes and don't retry
  // the network — the recovery poller restores it. Mirrors utils/connectivity's
  // isConnected() for reactive (template/store) consumers.
  const connected = computed(() => !isOffline.value && !serverUnavailable.value)

  const isWifi = computed(() => radioConnectionType.value === 'wifi')

  const startupNavigation = createStartupNavigationIntent()

  const navigateToNotifications = () => {
    startupNavigation.claim()
    useUiStore().closeDialogsBlockingNavigation()
    void router.push(NOTIFICATION_TAP_ROUTE)
  }

  const initInfo = async () => {
    await readRadioStatusFromDevice()
    const deviceInfo = await Device.getInfo()
    platform.value = deviceInfo.platform
    isWeb.value = deviceInfo.platform === 'web'
    const ins = await SafeArea.getSafeAreaInsets()
    insets.value = ins.insets
    await SafeArea.addListener('safeAreaChanged', ({ insets: changedInsets }) => {
      insets.value = changedInsets
    })

    if (Capacitor.isNativePlatform()) {
      await clearAppBadge()
      await App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          void clearAppBadge()
        }
      })

      if (Capacitor.getPlatform() === 'android') {
        await LocalNotifications.createChannel({
          id: HIGH_NOTIFICATION_CHANNEL_ID,
          name: 'Atsume',
          importance: 4,
          visibility: 1,
        })
      }

      await PushNotifications.addListener('registration', ({ value }) => {
        fcmToken.value = value
      })
      await PushNotifications.addListener('pushNotificationActionPerformed', () => {
        navigateToNotifications()
      })
      await PushNotifications.addListener('pushNotificationReceived', (notification) => {
        void LocalNotifications.schedule({
          notifications: [
            {
              id: Date.now() % 2147483647,
              title: notification.title ?? '',
              body: notification.body ?? '',
              channelId: HIGH_NOTIFICATION_CHANNEL_ID,
              extra: notification.data ?? {},
            },
          ],
        })
      })
      await LocalNotifications.addListener('localNotificationActionPerformed', () => {
        navigateToNotifications()
      })
      const { receive } = await PushNotifications.checkPermissions()
      if (receive === 'granted') {
        await PushNotifications.register()
      }
    }
  }

  return {
    platform,
    isWeb,
    isDev,
    init: initInfo,
    insets,
    isOffline,
    connected,
    isWifi,
    fcmToken,
    startupNavigationClaimed: startupNavigation.isClaimed,
    claimStartupNavigation: startupNavigation.claim,
  }
})
